/**
 * This component uses Portable Text to render a post body.
 *
 * You can learn more about Portable Text on:
 * https://www.sanity.io/docs/block-content
 * https://github.com/portabletext/react-portabletext
 * https://portabletext.org/
 *
 */

/* eslint-disable no-restricted-imports */
import {
  PortableText,
  type PortableTextBlock,
  type PortableTextComponents,
  stegaClean,
} from 'next-sanity'

/* eslint-enable no-restricted-imports */
import { Accordion } from '@/app/components/Accordion'
import { AdvertiserCallout } from '@/app/components/AdvertiserCallout'
import { Anchor } from '@/app/components/Anchor'
import { Button } from '@/app/components/Button'
import { Callout } from '@/app/components/Callout'
import { CheckListItem } from '@/app/components/CheckListItem'
import { ComparisonTable } from '@/app/components/ComparisonTable'
import { ContactForm } from '@/app/components/ContactForm'
import { ContentImage } from '@/app/components/ContentImage'
import { DatawrapperEmbed } from '@/app/components/DatawrapperEmbed'
import { DemoRequest } from '@/app/components/DemoRequest/DemoRequest'
import { DemoRequestForm } from '@/app/components/DemoRequest/DemoRequestForm'
import { ExternalVideo } from '@/app/components/ExternalVideo'
import { InlineBanner } from '@/app/components/InlineBanner'
import { LeadForm } from '@/app/components/LeadForm'
import { Group } from '@/app/components/PageBuilder/Group'
import { Spacing } from '@/app/components/PageBuilder/Spacing'
import { createPortableTextLists } from '@/app/components/portableText/lists'
import { createPortableTextMarks } from '@/app/components/portableText/marks'
import { PostVideo } from '@/app/components/PostVideo'
import { Publishers } from '@/app/components/Publishers'
import { Review } from '@/app/components/Review'
import { SocialEmbed } from '@/app/components/SocialEmbed'
import { Table } from '@/app/components/Table'
import { TableOfContents } from '@/app/components/TableOfContents'
import { TypeformEmbed } from '@/app/components/TypeformEmbed'
import { Workflow } from '@/app/components/Workflow'
import { YoutubeEmbed } from '@/app/components/YoutubeEmbed'
import {
  getHeadingClasses,
  type HeadingStyleConfig,
  type SubheadingStyleConfig,
} from '@/app/config/headingStyles'
import { TrackingLocation } from '@/app/utils/analytics'
import { ARTICLE_BLOCK_SPACING } from '@/app/utils/articleBlock'
import { getHeadingElements, type HeadingElement } from '@/app/utils/headingLevels'
import { cn } from '@/app/utils/style'
import { generateElementId } from '@/app/utils/text'

const defaultLinkClasses = '[&_a]:text-white-950 [&_a]:underline'

export default function CustomPortableText({
  className,
  value,
  tracking,
  headingConfig,
  buttonClassName,
  calloutClassName,
  blockquoteClassName,
  paragraphClassName,
  listsClassName,
  asFragment,
  documentId,
  documentType,
  parentPath,
  componentOverrides,
  headingsAsParagraphs = false,
  imageFit,
  wrapGroupLinks = false,
  variant = 'default',
}: {
  className?: string
  value: PortableTextBlock[]
  tracking: TrackingLocation
  /** Optional partial heading size configuration. Only override specific heading levels, others use defaults. */
  headingConfig?: Partial<HeadingStyleConfig>
  /** Optional partial subheading size configuration. Only override specific subheading levels, others use defaults. */
  subheadingConfig?: Partial<SubheadingStyleConfig>
  /** Optional className to apply to all buttons rendered in the content */
  buttonClassName?: string
  /** Optional className to apply to callout blocks (e.g. to lift their default max width) */
  calloutClassName?: string
  /** Optional className that replaces the default blockquote styling */
  blockquoteClassName?: string
  /** Optional className to apply to all paragraph elements */
  paragraphClassName?: string
  /** Optional className to apply to all list items */
  listsClassName?: string
  /** Render without wrapper div - useful when parent needs direct children (e.g., flex containers) */
  asFragment?: boolean
  /** Document ID for presentation mode - passed down from page/post */
  documentId?: string
  /** Document type for presentation mode - passed down from page/post */
  documentType?: string
  /** Parent path for presentation mode - e.g., 'pageBuilder[_key=="xyz"].content' */
  parentPath?: string
  /** Optional component overrides to customize specific block renderers */
  componentOverrides?: Partial<PortableTextComponents>
  /** Convert all heading elements (h1-h6) to paragraphs while preserving heading styles */
  headingsAsParagraphs?: boolean
  /** 'contain' renders contentImage blocks uncropped inside their aspect-ratio box (default 'cover') */
  imageFit?: 'cover' | 'contain'
  /** Lets links inside `group` blocks wrap (long-form articles with long URLs) */
  wrapGroupLinks?: boolean
  /** `article` (blog posts): chapter table of contents and article spacing around lead forms */
  variant?: 'default' | 'article'
}) {
  // Blog posts: the element follows the document structure, the look stays with the style
  const headingElements = variant === 'article' ? getHeadingElements(value) : null
  const headingElementOf = (block: { _key?: string }, style: HeadingElement): HeadingElement =>
    headingElements?.get(block._key ?? '') ?? style
  const baseComponents: PortableTextComponents = {
    block: {
      'normal': ({ children }) => (
        <p
          className={cn(
            'size-body-md text-white-800 my-lg',
            defaultLinkClasses,
            paragraphClassName,
          )}
        >
          {children}
        </p>
      ),
      'body-xs': ({ children }) => (
        <p className={cn('size-body-xs text-white-800', defaultLinkClasses, paragraphClassName)}>
          {children}
        </p>
      ),
      'body-sm': ({ children }) => (
        <p className={cn('size-body-sm text-white-800', defaultLinkClasses, paragraphClassName)}>
          {children}
        </p>
      ),
      'body-md': ({ children }) => (
        <p className={cn('size-body-md text-white-800', defaultLinkClasses, paragraphClassName)}>
          {children}
        </p>
      ),
      'body-lg': ({ children }) => (
        <p className={cn('size-body-lg text-white-800', defaultLinkClasses, paragraphClassName)}>
          {children}
        </p>
      ),
      'body-xl': ({ children }) => (
        <p className={cn('size-body-xl text-white-800', defaultLinkClasses, paragraphClassName)}>
          {children}
        </p>
      ),
      'd1': ({ children }) => (
        <p className={cn('size-display-d1 text-white-950', defaultLinkClasses, paragraphClassName)}>
          {children}
        </p>
      ),
      'd2': ({ children }) => (
        <p className={cn('size-display-d2 text-white-950', defaultLinkClasses, paragraphClassName)}>
          {children}
        </p>
      ),
      'h1': ({ children }) => {
        const id = generateElementId(children)
        const Element = headingsAsParagraphs ? 'p' : 'h1'
        return (
          <Element
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn('group relative', getHeadingClasses('h1', headingConfig), 'my-6xl')}
          >
            {children}
          </Element>
        )
      },
      'h2': ({ children, value: block }) => {
        const id = generateElementId(children)
        const Element = headingsAsParagraphs ? 'p' : headingElementOf(block, 'h2')
        return (
          <Element
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn('group relative', getHeadingClasses('h2', headingConfig), 'my-6xl')}
          >
            {children}
          </Element>
        )
      },
      'h3': ({ children, value: block }) => {
        const id = generateElementId(children)
        const Element = headingsAsParagraphs ? 'p' : headingElementOf(block, 'h3')
        return (
          <Element
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn(
              'group relative',
              getHeadingClasses('h3', headingConfig),
              'mt-5xl mb-3xl',
            )}
          >
            {children}
          </Element>
        )
      },
      'h4': ({ children, value: block }) => {
        const id = generateElementId(children)
        const Element = headingsAsParagraphs ? 'p' : headingElementOf(block, 'h4')
        return (
          <Element
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn(
              'group relative',
              getHeadingClasses('h4', headingConfig),
              'mt-5xl mb-2xl',
            )}
          >
            {children}
          </Element>
        )
      },
      'h5': ({ children, value: block }) => {
        const id = generateElementId(children)
        const Element = headingsAsParagraphs ? 'p' : headingElementOf(block, 'h5')
        return (
          <Element
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn('group relative', getHeadingClasses('h5', headingConfig), 'mt-5xl mb-xl')}
          >
            {children}
          </Element>
        )
      },
      'h6': ({ children, value: block }) => {
        const id = generateElementId(children)
        const Element = headingsAsParagraphs ? 'p' : headingElementOf(block, 'h6')
        return (
          <Element
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn('group relative', getHeadingClasses('h6', headingConfig), 'mt-5xl mb-xl')}
          >
            {children}
          </Element>
        )
      },
      'blockquote': ({ children }) => (
        <blockquote
          className={
            blockquoteClassName ??
            'size-body-sm lg:size-body-md py-2xl md:px-5xl md:pt-5xl lg:py-xl'
          }
        >
          {children}
        </blockquote>
      ),
      'quote-lg': ({ children }) => (
        <blockquote className="size-body-lg lg:size-body-xl py-2xl md:px-5xl md:pt-5xl lg:py-xl">
          {children}
        </blockquote>
      ),
    },
    types: {
      accordion: ({ value }) => (
        <Accordion
          value={value}
          tracking={tracking}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      anchor: ({ value }) => <Anchor anchorId={value.anchorId} />,
      button: ({ value }) => {
        const button = <Button button={value} tracking={tracking} className={buttonClassName} />
        // Centered call-to-action buttons inside articles
        return stegaClean(value.alignment) === 'center' ? (
          <div className="my-3xl flex justify-center">{button}</div>
        ) : (
          button
        )
      },
      advertiserCallout: ({ value: advertiserCallout }) => (
        <AdvertiserCallout
          advertiserCallout={advertiserCallout}
          tracking={tracking}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      callout: ({ value: callout }) => (
        <Callout
          callout={callout}
          className={calloutClassName}
          tracking={tracking}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      video: ({ value }) => <PostVideo video={value} />,
      checkListItem: ({ value }) => (
        <CheckListItem size={value.size || 'm'}>{value.text}</CheckListItem>
      ),
      comparisonTable: ({ value }) => <ComparisonTable value={value} />,
      contentBlock: ({ value }) =>
        value.content ? (
          <CustomPortableText
            value={value.content as PortableTextBlock[]}
            tracking={tracking}
            headingConfig={headingConfig}
            buttonClassName={buttonClassName}
            calloutClassName={calloutClassName}
            blockquoteClassName={blockquoteClassName}
            paragraphClassName={paragraphClassName}
            listsClassName={listsClassName}
            className={className}
            documentId={documentId}
            documentType={documentType}
            parentPath={parentPath}
            componentOverrides={componentOverrides}
            headingsAsParagraphs={headingsAsParagraphs}
            imageFit={imageFit}
            wrapGroupLinks={wrapGroupLinks}
            variant={variant}
          />
        ) : null,
      contactForm: ({ value: contactForm }) => (
        <ContactForm
          contactForm={contactForm}
          tracking={tracking}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      datawrapperEmbed: ({ value }) => <DatawrapperEmbed value={value} />,
      demoForm: ({ value: demoForm }) => (
        <DemoRequest
          demoForm={demoForm}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      demoRequestForm: ({ value }) => (
        <DemoRequestForm
          tracking={tracking}
          intro={value.intro}
          hideFormHeader={value.hideFormHeader}
          className="max-w-[680px]"
          displayMode="page"
        />
      ),
      leadForm: ({ value }) => {
        const form = <LeadForm tracking={tracking} alignment={value.alignment} />
        // Articles space every block evenly; site pages place the form with their own spacing blocks
        return variant === 'article' ? <div className={ARTICLE_BLOCK_SPACING}>{form}</div> : form
      },
      contentImage: ({ value }) => (
        <ContentImage
          value={value}
          fit={imageFit}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      contentTable: ({ value }) => <Table value={value} variant={variant} />,
      inlineBanner: ({ value }) => <InlineBanner value={value} tracking={tracking} />,
      publishers: ({ value }) => <Publishers value={value} />,
      review: ({ value }) => <Review value={value} tracking={tracking} />,
      externalVideo: ({ value }) => <ExternalVideo value={value} />,
      socialEmbed: ({ value }) => <SocialEmbed value={value} />,
      typeformEmbed: ({ value }) => <TypeformEmbed value={value} />,
      workflow: ({ value }) => <Workflow value={value} />,
      youtubeEmbed: ({ value }) => <YoutubeEmbed value={value} />,
      group: ({ value }) => (
        <Group
          value={value}
          imageFit={imageFit}
          wrapLinks={wrapGroupLinks}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      tableOfContents: () => (
        <TableOfContents
          allBlocks={value}
          variant={variant === 'article' ? 'chapters' : 'numbered'}
        />
      ),
      separator: () => <hr className="my-6xl" />,
      spacing: Spacing,
    },
    marks: createPortableTextMarks(tracking),
    ...createPortableTextLists(listsClassName),
  }

  // Merge component overrides (deep merge for block types)
  let components: PortableTextComponents = baseComponents

  if (componentOverrides) {
    components = {
      ...baseComponents,
      ...componentOverrides,
    }

    // Deep merge block components if both exist
    if (baseComponents.block && componentOverrides.block) {
      if (
        typeof baseComponents.block === 'object' &&
        typeof componentOverrides.block === 'object'
      ) {
        components.block = {
          ...baseComponents.block,
          ...componentOverrides.block,
        }
      }
    }
  }

  if (asFragment) {
    return <PortableText components={components} value={value} />
  }

  return (
    <div
      className={cn(
        'text-white-950 [&_a]:text-brand [&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className,
      )}
    >
      <PortableText components={components} value={value} />
    </div>
  )
}
